import Vue from "vue"
import Vuex from "vuex"
import { classifyValidationScore } from "@osteon-nexus-connect-lib/nc-frontend"
import oftClient from "@/lib/OftApi"

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    stepIndex: 1,
    analyzerSession: null,

    identifications: null,
    selectedIdentity: null,
    country: null,

    kitBoxID: null,
    kitSerialNumber: null,
    sessionType: null,
    ncSessionID: null,

    settings: null,
    pageState: [{ cols: 7, component: "ConnectBranding" }],
    transition: null,

    retrying: false,
    retryCount: 0,
    faceCount: null,
    uploadedScans: [],
    scanFiles: [],
    scanUploadError: false,
    scanBodySelectionLabel: null,
    narrowScanBodiesPresent: false,
    addScansResponse: null,
    selectGaugesResponse: null,
    validateScansResponse: null,
    libraryAssociationEnabled: false,
    selectedGaugeLibraries: [],
    selectedGaugeIndex: -1,
    libraryAssociationResponse: null,
    libraryAssociationViewOptions: null,
    validatedScanFiles: null,

    exportComponents: null,

    errorMessage: null,
    adminDialog: false,
    clientSearch: [],
    clientSessions: []
  },
  getters: {
    stepIndex: (state) => state.stepIndex,
    analyzerSession: (state) => state.analyzerSession,
    identifications: (state) => state.identifications,
    selectedIdentity: (state) => state.selectedIdentity,
    country: (state) => state.country,
    identity: (_state, getters) => {
      if (!getters.identifications) return
      return getters.identifications.actors.find(
        (a) => a.uid === getters.selectedIdentity
      )
    },
    isEmployee: (_state, getters) => {
      if (!getters.identity) return false
      if (getters.identity.actor_type === "employee") return true
      return false
    },
    kitBoxID: (state) => state.kitBoxID,
    kitSerialNumber: (state) => state.kitSerialNumber,
    sessionType: (state) => state.sessionType,
    ncSessionID: (state) => state.ncSessionID,
    settings: (state) => state.settings,
    pageState: (state) => state.pageState,
    scanValidationScore: (state) => state.scanValidationScore,
    transition: (state) => state.transition,
    retrying: (state) => state.retrying,
    retryCount: (state) => state.retryCount,
    faceCount: (state) => state.faceCount,
    uploadedScans: (state) => state.uploadedScans,
    scanFiles: (state) => state.scanFiles,
    scanUploadError: (state) => state.scanUploadError,
    narrowScanBodiesPresent: (state) => state.narrowScanBodiesPresent,
    scanBodySelectionLabel: (state) => state.scanBodySelectionLabel,
    addScansResponse: (state) => state.addScansResponse,
    validateScansResponse: (state) => state.validateScansResponse,
    validationReconsider: (state) => {
      if (!state.validateScansResponse) return
      return (
        classifyValidationScore(
          state.validateScansResponse,
          state.sessionType === "immediate"
        ) === "Consider Rescan"
      )
    },
    validationPass: (state) => {
      if (!state.validateScansResponse) return
      return (
        classifyValidationScore(
          state.validateScansResponse,
          state.sessionType === "immediate"
        ) !== "Fail"
      )
    },
    libraryAssociationEnabled: (state) => state.libraryAssociationEnabled,
    selectedGaugeLibraries: (state) => state.selectedGaugeLibraries,
    selectedGaugeIndex: (state) => state.selectedGaugeIndex,
    libraryAssociationResponse: (state) => state.libraryAssociationResponse,
    libraryAssociationViewOptions: (state) =>
      state.libraryAssociationViewOptions,
    validatedScanFiles: (state) => state.validatedScanFiles,
    exportComponents: (state) => state.exportComponents,
    errorMessage: (state) => state.errorMessage,
    adminDialog: (state) => state.adminDialog,
    clientSearch: (state) => state.clientSearch,
    clientSessions: (state) => state.clientSessions
  },
  mutations: {
    SET_STEP_INDEX(state, payload) {
      state.stepIndex = payload
    },
    SET_ANALYZER_SESSION(state, payload) {
      state.analyzerSession = payload
    },
    SET_IDENTIFICATIONS(state, payload) {
      state.identifications = payload
    },
    SET_SELECTED_IDENTITY(state, payload) {
      state.selectedIdentity = payload
    },
    SET_COUNTRY(state, payload) {
      state.country = payload
      oftClient.setCountry(payload)
    },
    SET_KIT_BOX_ID(state, payload) {
      state.kitBoxID = payload
    },
    SET_KIT_SERIAL_NUMBER(state, payload) {
      state.kitSerialNumber = payload
    },
    SET_SESSION_TYPE(state, payload) {
      state.sessionType = payload
    },
    SET_NC_SESSION_ID(state, payload) {
      state.ncSessionID = payload
    },
    SET_SETTINGS(state, payload) {
      state.settings = payload
    },
    SET_PAGE_STATE(state, payload) {
      state.pageState = payload
    },
    SET_SCAN_VALIDATION_SCORE(state, payload) {
      state.scanValidationScore = payload
    },
    SET_TRANSITION(state, payload) {
      state.transition = payload
    },
    SET_UPLOADED_SCANS(state, payload) {
      state.uploadedScans = payload
    },
    SET_SCAN_FILES(state, payload) {
      state.scanFiles = payload
    },
    SET_SCAN_UPLOAD_ERROR(state, payload) {
      state.scanUploadError = payload
    },
    SET_NARROW_SCAN_BODIES_PRESENT(state, payload) {
      state.narrowScanBodiesPresent = payload
    },
    SET_SCAN_BODY_SELECTION_LABEL(state, payload) {
      state.scanBodySelectionLabel = payload
    },
    SET_ADD_SCANS_RESPONSE(state, payload) {
      state.addScansResponse = payload
    },
    SET_VALIDATE_SCANS_RESPONSE(state, payload) {
      state.validateScansResponse = payload
    },
    SET_MODEL_SCAN(state, payload) {
      state.modelScan = payload
    },
    SET_RETRYING(state, payload) {
      state.retrying = payload
    },
    SET_RETRY_COUNT(state, payload) {
      state.retryCount = payload
    },
    SET_FACE_COUNT(state, payload) {
      state.faceCount = payload
    },
    SET_LIBRARY_ASSOCIATION_ENABLED(state, payload) {
      state.libraryAssociationEnabled = payload
    },
    SET_SELECTED_GAUGE_LIBRARIES(state, payload) {
      state.selectedGaugeLibraries = payload
    },
    SET_SELECTED_GAUGE_INDEX(state, payload) {
      state.selectedGaugeIndex = payload
    },
    SET_LIBRARY_ASSOCIATION_RESPONSE(state, payload) {
      state.libraryAssociationResponse = payload
    },
    SET_LIBRARY_ASSOCIATION_VIEW_OPTIONS(state, payload) {
      state.libraryAssociationViewOptions = payload
    },
    SET_VALIDATED_SCAN_FILES(state, payload) {
      state.validatedScanFiles = payload
    },
    SET_EXPORT_COMPONENTS(state, payload) {
      state.exportComponents = payload
    },
    SET_ERROR_MESSAGE(state, payload) {
      state.errorMessage = payload
    },
    SET_ADMIN_DIALOG(state, payload) {
      state.adminDialog = payload
    },
    SET_CLIENT_SEARCH(state, payload) {
      state.clientSearch = payload
    },
    SET_CLIENT_SESSIONS(state, payload) {
      state.clientSessions = payload
    }
  },
  actions: {
    setStepIndex({ commit }, payload) {
      commit("SET_STEP_INDEX", payload)
    },
    setAnalyzerSession({ commit }, payload) {
      commit("SET_ANALYZER_SESSION", payload)
    },
    setIdentifications({ commit }, payload) {
      commit("SET_IDENTIFICATIONS", payload)
    },
    setSelectedIdentity({ commit }, payload) {
      commit("SET_SELECTED_IDENTITY", payload)
    },
    setCountry({ commit }, payload) {
      commit("SET_COUNTRY", payload)
    },
    setKitBoxID({ commit }, payload) {
      commit("SET_KIT_BOX_ID", payload)

      if (payload) {
        const serialNumber = payload.kit_box_id
          .split(`${payload.kit_box_prefix}_`)
          .pop()
        commit("SET_KIT_SERIAL_NUMBER", serialNumber)
      } else commit("SET_KIT_SERIAL_NUMBER", null)
    },
    setKitSerialNumber({ commit }, payload) {
      commit("SET_KIT_SERIAL_NUMBER", payload)
    },
    setSessionType({ commit }, payload) {
      commit("SET_SESSION_TYPE", payload)
    },
    setNCSessionID({ commit }, payload) {
      commit("SET_NC_SESSION_ID", payload)
    },
    setSettings({ commit }, payload) {
      commit("SET_SETTINGS", payload)
    },
    setPageState({ commit }, payload) {
      commit("SET_PAGE_STATE", payload)
    },
    setScanValidationScore({ commit }, payload) {
      commit("SET_SCAN_VALIDATION_SCORE", payload)
    },
    setTransition({ commit }, payload) {
      commit("SET_TRANSITION", payload)
    },
    setNarrowScanBodiesPresent({ commit }, payload) {
      commit("SET_NARROW_SCAN_BODIES_PRESENT", payload)
    },
    setScanBodySelectionLabel({ commit }, payload) {
      commit("SET_SCAN_BODY_SELECTION_LABEL", payload)
    },
    setAddScansResponse({ commit }, payload) {
      commit("SET_ADD_SCANS_RESPONSE", payload)
    },
    setUploadedScans({ commit }, payload) {
      commit("SET_UPLOADED_SCANS", payload)
    },
    setScanFiles({ commit }, payload) {
      commit("SET_SCAN_FILES", payload)
    },
    setScanUploadError({ commit }, payload) {
      commit("SET_SCAN_UPLOAD_ERROR", payload)
    },
    setValidateScansResponse({ commit }, payload) {
      commit("SET_VALIDATE_SCANS_RESPONSE", payload)
    },
    setModelScan({ commit }, payload) {
      commit("SET_MODEL_SCAN", payload)
    },
    setRetrying({ commit }, payload) {
      commit("SET_RETRYING", payload)
    },
    setRetryCount({ commit }, payload) {
      commit("SET_RETRY_COUNT", payload)
    },
    setFaceCount({ commit }, payload) {
      commit("SET_FACE_COUNT", payload)
    },
    setLibraryAssociationEnabled({ commit }, payload) {
      commit("SET_LIBRARY_ASSOCIATION_ENABLED", payload)
    },
    setSelectedGaugeLibraries({ commit }, payload) {
      commit("SET_SELECTED_GAUGE_LIBRARIES", payload)
    },
    setSelectedGaugeIndex({ commit }, payload) {
      commit("SET_SELECTED_GAUGE_INDEX", payload)
    },
    setLibraryAssociationResponse({ commit }, payload) {
      commit("SET_LIBRARY_ASSOCIATION_RESPONSE", payload)
    },
    setLibraryAssociationViewOptions({ commit }, payload) {
      commit("SET_LIBRARY_ASSOCIATION_VIEW_OPTIONS", payload)
    },
    setValidatedScanFiles({ commit }, payload) {
      commit("SET_VALIDATED_SCAN_FILES", payload)
    },
    setExportComponents({ commit }, payload) {
      commit("SET_EXPORT_COMPONENTS", payload)
    },
    setErrorMessage({ commit }, payload) {
      commit("SET_ERROR_MESSAGE", payload)
    },
    restartSession({ dispatch }) {
      dispatch("setAnalyzerSession", null)
      dispatch("setKitBoxID", null)
      dispatch("setSessionType", null)
      dispatch("setNCSessionID", null)
      dispatch("setUploadedScans", [])
      dispatch("setScanFiles", [])
      dispatch("setScanUploadError", false)
      dispatch("setNarrowScanBodiesPresent", false)
      dispatch("setScanBodySelectionLabel", "Scan Gauges")
      dispatch("setAddScansResponse", null)
      dispatch("setFaceCount", null)
      dispatch("setRetryCount", 0)
      dispatch("setRetrying", false)
      dispatch("setValidateScansResponse", null)
      dispatch("setPageState", [{ cols: 7, component: "ConnectBranding" }])
    },
    newSession({ dispatch }) {
      dispatch("restartSession")
      dispatch("setPageState", [{ cols: 7, component: "ConnectBranding" }])
    },
    setAdminDialog({ commit }, payload) {
      commit("SET_ADMIN_DIALOG", payload)
    },
    setClientSearch({ commit }, payload) {
      commit("SET_CLIENT_SEARCH", payload)
    },
    setClientSessions({ commit }, payload) {
      commit("SET_CLIENT_SESSIONS", payload)
    }
  },
  modules: {}
})
